import { Injectable } from '@angular/core';
import { interval, Observable, of } from 'rxjs';
import { catchError, map, mapTo, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { LoggerService } from '../../../services/logger/logger.service';
import { LoginStateService } from '../../login/services/login-state.service';
import { ScanPartnerCodeInfo } from '../../partner-code/models/scan-partner-code-info';
import { SmartCardStatus } from '../../smart-card/models/smart-card-status';
import { SmartCardCommunicationService } from '../../smart-card/services/smart-card-communication.service';
import { VuConnectionHttpService } from '../../vu/services/vu-connection-http.service';
import { RefundStepEnum } from '../models/refund-step-enum';
import { RefundCardOrderService } from './refund-card-order.service';
import { RefundCardPartnerService } from './refund-card-partner.service';
@Injectable()
export class RefundCardStateService {

  private _state: RefundStepEnum = RefundStepEnum.None;

  private _steps: RefundStepEnum[] = [
    RefundStepEnum.None,
    RefundStepEnum.InsertCard,
    RefundStepEnum.ReturnCard,
    RefundStepEnum.Login,
    RefundStepEnum.Balance,
    RefundStepEnum.Refund,
  ];

  private _stepIndex = -1;

  private _cardBalance = 0;
  private _availableAmount = 0;
  private _guestCardPrice = 0;
  private _scanPartnerCodeInfo: ScanPartnerCodeInfo;

  constructor(
    private smartCardCommunicationService: SmartCardCommunicationService,
    private loggerService: LoggerService,
    private vuConnectionHttpService: VuConnectionHttpService,
    private loginStateService: LoginStateService,
    private refundCardOrderService: RefundCardOrderService,
    private refundCardPartnerService: RefundCardPartnerService,
  ) {
  }

  get state(): RefundStepEnum {
    return this._state;
  }

  reset(): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        this._stepIndex = -1;
        this._state = RefundStepEnum.None;
        this._cardBalance = 0;
        this._availableAmount = 0;
        this._guestCardPrice = 0;

        return this.nextStep();
      }),
    );
  }

  nextStep(): Observable<boolean> {
    return this.getNextStep().pipe(
      switchMap(state => {
        if (state) {
          return this.update(state);
        }
        return of(false);
      })
    );
  }

  getNextStep(): Observable<RefundStepEnum> {
    return of(null).pipe(
      switchMap(_ => {
        const steps = this._steps;
        this._stepIndex++;

        while (steps.length > this._stepIndex) {
          const nextStep = steps[this._stepIndex];
          if (nextStep === RefundStepEnum.ReturnCard && this.isCardRegistered && !this.isPersonalCard) {
            this._stepIndex++;
            continue;
          }

          return this.isAvailableState(nextStep).pipe(
            switchMap(stateAvailable => {
              if (stateAvailable) {
                return of(nextStep);
              }
              return this.getNextStep();
            }),
          );
        }

        this._stepIndex = -1;
        return of(null);
      })
    );
  }

  prevStep(): Observable<boolean> {
    return this.getPrevStep().pipe(
      switchMap(state => {
        if (state) {
          return this.update(state);
        }
        return of(false);
      })
    );
  }

  getPrevStep(): Observable<RefundStepEnum> {
    return of(null).pipe(
      switchMap(_ => {
        const steps = this._steps;
        if (steps.length <= this._stepIndex) {
          this._stepIndex = steps.length - 1;
        }
        this._stepIndex--;
        while (this._stepIndex >= 0) {
          const prevStep = steps[this._stepIndex];
          return this.isAvailableState(prevStep).pipe(
            switchMap(stateAvailable => {
              if (stateAvailable) {
                return of(prevStep);
              }
              return this.getPrevStep();
            }),
          );
        }
        this._stepIndex = -1;
        return of(null);
      })
    );
  }

  isAvailableState(stepRefundStepsEnum: RefundStepEnum): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        switch (stepRefundStepsEnum) {
          case RefundStepEnum.ReturnCard:
            return this.isCardInReader;
        }
        return of(true);
      }),
    );
  }

  update(state: RefundStepEnum): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        const prevState = this._state;
        this.loggerService.info(`RefoundCardStateService: update. change state from '${prevState}' to '${state}'`);
        this._state = state;
        switch (state) {
          case RefundStepEnum.None:
            if (prevState === RefundStepEnum.None) {
              return this.checkCardDispenserStatus().pipe(
                switchMap(__ => this.nextStep()),
              );
            } else {
              return this.closeShutter().pipe(
                mapTo(false),
              );
            }
          case RefundStepEnum.ReturnCard:
            return this.returnCard().pipe(
              switchMap(__ => {
                this._scanPartnerCodeInfo = null;
                return this.prevStep();
              }),
            );
          case RefundStepEnum.InsertCard:
            return this.takeCard(this.takenCardTimeoutSec).pipe(
              switchMap(cardUID => {
                if (!cardUID) {
                  return of(false);
                }
                return this.refundCardPartnerService.getPartnerInfo(cardUID).pipe(
                  map(scanPartnerCodeInfo => {
                    this._scanPartnerCodeInfo = scanPartnerCodeInfo;
                    return !!scanPartnerCodeInfo;
                  })
                );
              }),
              switchMap(cardRead => {
                if (cardRead) {
                  return this.nextStep();
                } else {
                  return this.prevStep();
                }
              }),
            );
          case RefundStepEnum.Login:
            return this.loginStateService.logout().pipe(mapTo(true));
          case RefundStepEnum.Balance:
            return this.loadCardBalance().pipe(
              switchMap(balanceLoaded => {
                if (!balanceLoaded) {
                  return this.prevStep();
                }
                return of(true);
              }),
            );
          case RefundStepEnum.Refund:
            return this.refundCardOrderService.createOrder(this.availableAmount).pipe(
              tap(order => this.vuConnectionHttpService.redirectToExternalRefund(order ? order.id : 0)),
              mapTo(true),
            );
        }
        return of(true);
      }),
    );
  }

  get isCardInReader(): Observable<boolean> {
    return this.smartCardCommunicationService.getStatus().pipe(
      map((status: SmartCardStatus) => status && status.isCardInserted),
    );
  }

  checkCardDispenserStatus(): Observable<boolean> {
    return this.smartCardCommunicationService.ensureThatCardDispenserIsAvailable();
  }

  takeCard(waitTimeSec: number): Observable<string> {
    return this.smartCardCommunicationService.takeCard(waitTimeSec);
  }

  waitCard(waitTimeSec: number): Observable<boolean> {
    return this.smartCardCommunicationService.waitCard(waitTimeSec);
  }

  openShutter(): Observable<boolean> {
    return this.smartCardCommunicationService.openShutter();
  }

  closeShutter(): Observable<boolean> {
    return this.smartCardCommunicationService.closeShutter();
  }

  returnCard(): Observable<boolean> {
    return this.smartCardCommunicationService.releaseCard(this.returnCardTimeoutSec);
  }

  get returnCardTimeoutSec(): number {
    return 30;
  }

  get takenCardTimeoutSec(): number {
    return 30;
  }

  loadCardBalance(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this._availableAmount = -1;
        this._cardBalance = -1;
        this._guestCardPrice = -1;
      }),
      switchMap(_ => this.refundCardPartnerService.cardBalance),
      tap(balance => { this._cardBalance = balance; }),
      switchMap(_ => this.refundCardOrderService.guestCardProductPrice),
      tap(price => { this._guestCardPrice = price; }),
      switchMap(_ => this.howMuchCanPayoutAmount),
      map(balance => {
        this._availableAmount = balance;
        return true;
      }),
      catchError(error => {
        const errorMessage = error && error.message ? error.message : error;
        this.loggerService.error(`RefoundCardStateService: loadCardBalance. error: ${errorMessage}`);
        return of(false);
      }),
    );
  }

  get cardBalance(): number {
    return this._cardBalance;
  }

  get availableAmount(): number {
    return this._availableAmount;
  }

  get guestCardPrice(): number {
    return this._guestCardPrice;
  }

  get scanPartnerCodeInfo(): ScanPartnerCodeInfo {
    return this._scanPartnerCodeInfo;
  }

  get howMuchCanPayoutAmount(): Observable<number> {
    return of(this.cardBalance + this.guestCardPrice).pipe(
      switchMap(amount => this.vuConnectionHttpService.howMuchCanPayoutAmount(amount)),
    );
  }

  get isCardRegistered(): boolean {
    return this._scanPartnerCodeInfo && !!this._scanPartnerCodeInfo.partner;
  }

  get isPersonalCard(): boolean {
    return this._scanPartnerCodeInfo && this._scanPartnerCodeInfo.personalNumberExist;
  }

  get isActive(): boolean {
    return this.state !== RefundStepEnum.None;
  }
}
